<template>
	<div class="main">
		<div class="box">
			<Header ref="header" class="support-header"></Header>
			<img class="loading support-top-bg" :src="gameData.topBgImage" alt="">
			<div class="item_Options">
				<div class="content">
					<UserInfo ref="userInfo" class="support-user-info"></UserInfo>

					<section class="support-workbench">
						<aside class="side-panel">
							<section class="player-card">
								<div class="player-head">
									<span class="player-avatar" aria-hidden="true">
										<i class="el-icon-user"></i>
									</span>
									<div>
										<p class="muted">{{ $t('game_role_text') }}</p>
										<h2>{{ roleInfo.roleName || '-' }}</h2>
									</div>
								</div>
								<dl class="meta-list">
									<div>
										<dt>{{ $t('game_server_text') }}</dt>
										<dd>{{ roleInfo.serverName || roleInfo.serverCode || '-' }}</dd>
									</div>
									<div>
										<dt>{{ $t('support_user_id') }}</dt>
										<dd>{{ userId }}</dd>
									</div>
									<div>
										<dt>{{ $t('support_game') }}</dt>
										<dd>{{ gameData.gameName || '-' }}</dd>
									</div>
									<div>
										<dt>{{ $t('support_platform') }}</dt>
										<dd>{{ platformName }}</dd>
									</div>
								</dl>
							</section>

							<section class="tips-card">
								<h3>{{ $t('support_form_type_label') }}</h3>
								<div class="quick-list">
									<button
										v-for="opt in typeOptions"
										:key="opt.value"
										type="button"
										class="quick-item"
										:class="{ active: showForm && defaultTicketType === opt.value }"
										@click="createTicket(opt.value)"
									>
										<span>{{ $t(opt.labelKey) }}</span>
										<small>{{ typeCode(opt) }}</small>
									</button>
								</div>
							</section>
						</aside>

						<section class="workspace" :class="{ 'chat-workspace': showChat }">
							<nav class="tabs" :aria-label="$t('support_tabs_label')">
								<button
									class="tab"
									:class="{ active: showForm }"
									type="button"
									@click="createTicket(defaultTicketType || typeOptions[0].value)"
								>
									<span class="tab-icon submit-icon"></span>
									<span>{{ $t('support_submit_issue') }}</span>
								</button>
								<button
									class="tab"
									:class="{ active: !showForm }"
									type="button"
									@click="showTicketList(false)"
								>
									<span class="tab-icon list-icon"></span>
									<span>{{ $t('support_my_questions') }}</span>
									<em v-if="ticketCount">{{ ticketCount }}</em>
								</button>
							</nav>

							<section
								ref="listWrap"
								class="view active"
								:class="{ 'detail-host': showChat }"
								@scroll="onListScroll"
							>
								<SupportForm
									v-if="showForm"
									:type-options="typeOptions"
									:default-type="defaultTicketType"
									@submit="handleSubmit"
									@cancel="handleCancel"
								/>

								<SupportChat
									v-else-if="showChat"
									:ticket="activeTicket"
									:current-ticket="currentTicket"
									:support-session="supportSession"
									:aqId="aqId"
									:title="supportTitle"
									@back="closeChat"
									@refresh="openChat(aqId)"
								/>

								<div v-else class="tickets-view">
									<div v-if="tickets.length" class="ticket-list">
										<TicketCard
											v-for="(item, idx) in tickets"
											:key="item.aqId || idx"
											:item="item"
											@click-status="openChat(item.aqId)"
										/>
									</div>

									<div v-else class="empty-state">
										<strong>{{ $t('support_my_questions') }}</strong>
										<p>{{ $t('no_data_text') }}</p>
									</div>

									<button v-if="hasMore" class="load-more visible" type="button" @click="loadMore">
										{{ $t('support_load_more') }}
									</button>
									<div v-if="isLoading" class="support-loading">
										<i class="el-icon-loading"></i>
										<span>{{ $t('support_submitting') }}</span>
									</div>
								</div>
							</section>
						</section>

						<div
							v-if="closeDialogVisible"
							class="survey-mask"
							role="dialog"
							aria-modal="true"
							:aria-label="$t('support_survey_title')"
							@click.self="dismissCloseDialog"
						>
							<section class="survey-dialog">
								<button class="survey-close" type="button" :aria-label="$t('support_survey_close')" @click="dismissCloseDialog">
									<i class="el-icon-close"></i>
								</button>
								<div class="survey-main">
									<span class="survey-icon" aria-hidden="true">?</span>
									<div class="survey-copy">
										<h3>{{ $t('support_survey_title') }}</h3>
										<p>{{ $t('support_survey_message') }}</p>
									</div>
								</div>
								<div class="survey-actions">
									<button class="survey-button ghost" type="button" @click="cancelCloseChat">
										{{ $t('support_survey_close') }}
									</button>
									<button class="survey-button primary" type="button" @click="confirmCloseChat">
										{{ $t('support_mark_solved') }}
									</button>
								</div>
							</section>
						</div>
					</section>
				</div>
			</div>
			<Footer class="support-footer"></Footer>
		</div>
	</div>
</template>

<script>
import Header from '../../components/header.vue'
import Footer from '../../components/footer.vue'
import UserInfo from '../../components/userInfo.vue'
import TicketCard from './modules/TicketCard.vue'
import SupportForm from './modules/SupportForm.vue'
import SupportChat from './modules/SupportChat.vue'
import {
	buildSupportSession,
	hasSupportSession,
	getTicketList,
	createTicket as createSupportTicket,
	getTicketDetail,
	markTicketRead,
	markTicketResolved,
	unwrapResult
} from '../../utils/api/supportApi.js'

export default {
	name: 'supportIndex',
	components: {
		Header,
		Footer,
		UserInfo,
		TicketCard,
		SupportForm,
		SupportChat
	},
	data() {
		return {
			gameData: this.$store.state.gameData || {},
			hasLogin: this.$session.hasLogin(),
			loginData: this.$cache.getLoginData() || {},
			roleInfo: this.$cache.getRoleInfo() || {},
			tickets: [],
			pageNo: 1,
			pageSize: 5,
			total: 0,
			unreadReplyCount: 0,
			isLoading: false,
			initialLoaded: false,
			typeOptions: [
				{ value: 'RECHARGE', labelKey: 'support_type_topup' },
				{ value: 'ACCOUNT', labelKey: 'support_type_account' },
				{ value: 'GUIDE', labelKey: 'support_type_guide' },
				{ value: 'SUGGESTION', labelKey: 'support_type_suggestion' },
				{ value: 'REPORT', labelKey: 'support_type_report' },
				{ value: 'OTHER', labelKey: 'support_type_other' }
			],
			showForm: false,
			supportTitle: this.$t('support_my_questions'),
			showChat: false,
			activeTicket: [],
			currentTicket: null,
			aqId: null,
			defaultTicketType: '',
			closeDialogVisible: false,
			isMobile: false,
			submitting: false
		}
	},
	computed: {
		ticketCount() {
			return Number(this.unreadReplyCount || 0)
		},
		hasMore() {
			return this.tickets.length < Number(this.total || 0)
		},
		userId() {
			return this.$session.getUserId() || this.loginData.userId || this.loginData.id || '-'
		},
		platformName() {
			return this.loginData.platform || this.loginData.channel || 'meow'
		},
		supportSession() {
			return buildSupportSession({
				roleInfo: this.roleInfo,
				gameData: this.gameData,
				loginData: this.loginData
			})
		}
	},
	mounted() {
		this.changeBackground(this.gameData.bottomBgImage)
		this.resetPagination()
		this.initTickets().then(() => { this.initialLoaded = true })
		this.checkMobile()
		window.addEventListener('resize', this.checkMobile)
	},
	beforeDestroy() {
		window.removeEventListener('resize', this.checkMobile)
	},
	methods: {
		changeBackground(newPath) {
			if (newPath) {
				
			}
		},
		typeCode(opt) {
			const map = {
				RECHARGE: 'support_type_topup_code',
				ACCOUNT: 'support_type_account_code',
				GUIDE: 'support_type_guide_code',
				SUGGESTION: 'support_type_suggestion_code',
				REPORT: 'support_type_report_code',
				OTHER: 'support_type_other_code'
			}
			return this.$t(map[String(opt.value).toUpperCase()] || 'support_type_other_code')
		},
		resetPagination() {
			this.pageNo = 1
			this.total = 0
			this.unreadReplyCount = 0
			this.isLoading = false
			this.initialLoaded = false
			if (this.$refs.listWrap) this.$refs.listWrap.scrollTop = 0
		},
		async initTickets() {
			if (!hasSupportSession(this.supportSession)) {
				this.tickets = []
				this.total = 0
				this.unreadReplyCount = 0
				return
			}
			try {
				const response = await getTicketList(this.supportSession, this.pageNo, this.pageSize)
				const page = unwrapResult(response) || {}
				const records = page.records || []
				const rows = records.map(this.normalizeTicket)
				this.total = Number(page.total || rows.length || 0)
				this.unreadReplyCount = Number(page.unreadReplyCount || 0)
				if (this.pageNo === 1) {
					this.tickets = rows
				} else {
					this.tickets = this.tickets.concat(rows)
				}
			} catch (e) {
				if (this.pageNo === 1) {
					this.tickets = []
					this.total = 0
					this.unreadReplyCount = 0
					this.$message.error(e.message || this.$t('support_submit_fail'))
				} else {
					this.pageNo = Math.max(1, this.pageNo - 1)
					this.$message.error(e.message || this.$t('support_submit_fail'))
				}
			}
		},
		async openChat(aqId) {
			const matched = this.tickets.find(item => item.aqId === aqId) || {}
			try {
				const response = await getTicketDetail(this.supportSession, aqId)
				const data = unwrapResult(response) || {}
				const ticket = data.ticket || matched.raw || {}
				const replies = (data.replies || []).filter(item => {
					return item.visibleToPlayer !== 0 && item.messageType !== 'INTERNAL_NOTE' && item.messageType !== 'SYSTEM'
				})
				this.currentTicket = ticket
				this.activeTicket = replies.map(item => this.normalizeMessage(item, ticket))
				this.aqId = ticket.id || aqId
				this.showChat = true
				this.showForm = false
				this.supportTitle = ticket.originalContent || ticket.content || matched.outline || this.$t('support_my_questions')
				if (matched.hasUnreadReply) {
					markTicketRead(this.supportSession, aqId).catch(() => this.refreshTickets())
					matched.hasUnreadReply = false
					this.unreadReplyCount = Math.max(0, Number(this.unreadReplyCount || 0) - 1)
				}
			} catch {
				this.$message.error(this.$t('support_submit_fail'))
			}
		},
		closeChat() {
			const status = this.getLastStatus()
			if (this.isClosedStatus(status)) {
				this.showTicketList(true)
			} else {
				this.closeDialogVisible = true
			}
		},
		cancelCloseChat() {
			this.closeDialogVisible = false
			this.showTicketList(true)
		},
		dismissCloseDialog() {
			this.closeDialogVisible = false
		},
		async confirmCloseChat() {
			try {
				await markTicketResolved(this.supportSession, this.currentTicket || { id: this.aqId })
				this.closeDialogVisible = false
				this.$message.success(this.$t('support_close_success'))
				this.showTicketList(true)
			} catch {
				this.closeDialogVisible = false
				this.$message.error(this.$t('support_close_fail'))
			}
		},
		showTicketList(refresh = false) {
			this.showChat = false
			this.showForm = false
			this.supportTitle = this.$t('support_my_questions')
			this.activeTicket = []
			this.currentTicket = null
			this.aqId = null
			this.defaultTicketType = ''
			if (refresh) {
				this.refreshTickets()
			} else {
				this.$nextTick(() => {
					if (this.$refs.listWrap) this.$refs.listWrap.scrollTop = 0
				})
			}
		},
		refreshTickets() {
			this.resetPagination()
			this.initTickets().then(() => { this.initialLoaded = true })
			this.$nextTick(() => {
				if (this.$refs.listWrap) this.$refs.listWrap.scrollTop = 0
			})
		},
		checkMobile() {
			this.isMobile = window.innerWidth <= 767
		},
		getLastStatus() {
			const arr = this.activeTicket
			if (Array.isArray(arr) && arr.length) {
				const last = arr[arr.length - 1]
				return last && last.status
			}
			return this.currentTicket && this.currentTicket.status
		},
		async handleSubmit(payload) {
			if (this.submitting) return
			if (!hasSupportSession(this.supportSession)) {
				this.$message.error(this.$t('support_submit_fail'))
				return
			}
			this.submitting = true
			const loader = this.$loading({
				lock: true,
				text: this.$t('support_submitting'),
				spinner: 'el-icon-loading',
				background: 'rgba(0,0,0,0.15)'
			})
			const imageUrls = (payload.files || [])
				.map(file => file.url || (file.response && (file.response.url || (file.response.data && file.response.data.url))) || '')
				.filter(Boolean)

			try {
				await createSupportTicket(this.supportSession, {
					content: payload.description,
					attachments: imageUrls.join(','),
					category: payload.type,
					ip: this.supportSession.ip || ''
				})
				this.showTicketList(true)
			} catch {
				this.$message.error(this.$t('support_submit_fail'))
			} finally {
				this.submitting = false
				loader && loader.close()
			}
		},
		handleCancel() {
			this.showTicketList(false)
		},
		createTicket(type = '') {
			this.defaultTicketType = type || (this.typeOptions[0] && this.typeOptions[0].value) || ''
			this.showForm = true
			this.showChat = false
			this.activeTicket = []
			this.currentTicket = null
			this.aqId = null
			this.supportTitle = this.$t('support_submit_issue')
			this.$nextTick(() => {
				if (this.$refs.listWrap) this.$refs.listWrap.scrollTop = 0
			})
		},
		normalizeTicket(item) {
			const ticketId = item.id || item.ticketId || item.aqId || item.ticketNo
			return {
				aqId: ticketId,
				ticketNo: item.ticketNo || ticketId,
				category: this.categoryLabel(item.category || item.type),
				categoryCode: item.category || item.type,
				outline: item.originalContent || item.content || item.outline || '-',
				time: this.formatDate(item.lastPlayerMessageTime || item.createTime || item.time),
				score: item.satisfactionScore || item.score || 0,
				status: item.status || 'NEW',
				hasUnreadReply: !!item.hasUnreadReply,
				raw: item
			}
		},
		normalizeMessage(item, ticket) {
			const isPlayer = item.senderType === 'PLAYER' || item.messageType === 'PLAYER_REPLY'
			return {
				sender: isPlayer ? 0 : 1,
				senderName: item.senderName,
				message: item.messageStatus === 1 ? '' : (item.content || item.message || ''),
				messageStatus: item.messageStatus,
				time: item.createTime || item.time,
				status: ticket && ticket.status,
				score: ticket && ticket.satisfactionScore,
				imgUrl: item.attachments || item.imgUrl || '',
				attachments: item.attachments || item.imgUrl || '',
				raw: item
			}
		},
		categoryLabel(value) {
			const key = String(value || 'OTHER').toUpperCase()
			const map = {
				RECHARGE: 'support_type_topup',
				ACCOUNT: 'support_type_account',
				GUIDE: 'support_type_guide',
				SUGGESTION: 'support_type_suggestion',
				REPORT: 'support_type_report',
				OTHER: 'support_type_other',
				1: 'support_type_topup',
				2: 'support_type_account',
				3: 'support_type_guide',
				4: 'support_type_suggestion',
				5: 'support_type_event',
				6: 'support_type_report'
			}
			return this.$t(map[key] || 'support_type_other') || key
		},
		isClosedStatus(status) {
			const key = String(status || '').toUpperCase()
			return key === 'RESOLVED' || key === 'CANCELLED' || key === '2' || key === '3'
		},
		formatDate(value, onlyDate = false) {
			if (!value) return '-'
			const date = new Date(value)
			if (Number.isNaN(date.getTime())) return String(value)
			const pad = num => String(num).padStart(2, '0')
			const day = `${date.getFullYear()}.${pad(date.getMonth() + 1)}.${pad(date.getDate())}`
			if (onlyDate) return day
			return `${day} ${pad(date.getHours())}:${pad(date.getMinutes())}:${pad(date.getSeconds())}`
		},
		loadMore() {
			if (this.isLoading || !this.hasMore) return
			this.isLoading = true
			this.pageNo += 1
			this.initTickets().finally(() => {
				this.isLoading = false
			})
		},
		onListScroll() {
			const el = this.$refs.listWrap
			if (!el || this.isLoading || this.showChat || this.showForm) return
			if (!this.initialLoaded) return
			if (el.scrollTop === 0) return
			const nearBottom = el.scrollTop + el.clientHeight >= el.scrollHeight - 50
			if (nearBottom && this.hasMore) this.loadMore()
		}
	}
}
</script>

<style scoped>
.support-workbench {
	--support-primary: var(--theme-color, #D26A2C);
	--support-primary-strong: var(--theme-color, #D26A2C);
	--support-primary-soft: color-mix(in srgb, var(--theme-color, #D26A2C) 10%, #fff);
	--support-primary-line: color-mix(in srgb, var(--theme-color, #D26A2C) 36%, #dfe7f1);
	--support-bg: #f3f6fb;
	--support-panel: #ffffff;
	--support-line: #dfe7f1;
	--support-line-soft: #edf2f7;
	--support-text: #172033;
	--support-muted: #7b8798;
	display: grid;
	grid-template-columns: 2.5rem minmax(0, 1fr);
	gap: .14rem;
	width: 100%;
	margin: .12rem 0 0;
	color: var(--support-text);
	font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
}

.side-panel {
	display: flex;
	min-height: 0;
	flex-direction: column;
	gap: .1rem;
}

.player-card,
.tips-card,
.workspace {
	border: 1px solid var(--support-line);
	border-radius: .08rem;
	background: var(--support-panel);
}

.player-card {
	padding: .16rem;
}

.player-head {
	display: flex;
	align-items: center;
	gap: .12rem;
	padding-bottom: .12rem;
	border-bottom: 1px solid var(--support-line-soft);
}

.player-avatar {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 .4rem;
	width: .4rem;
	height: .4rem;
	border-radius: .08rem;
	color: var(--support-primary);
	font-size: .175rem;
	line-height: 1;
	background: var(--support-primary-soft);
	box-shadow: inset 0 0 0 1px var(--support-primary-line);
}

.muted {
	margin: 0;
	color: var(--support-muted);
	font-size: .175rem;
	line-height: .2rem;
}

.player-head h2 {
	max-width: 1.72rem;
	margin: .01rem 0 0;
	overflow: hidden;
	color: var(--support-text);
	font-size: .175rem;
	font-weight: 800;
	line-height: .25rem;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.meta-list {
	display: grid;
	gap: .1rem;
	margin: .12rem 0 0;
}

.meta-list dt {
	color: var(--support-muted);
	font-size: .175rem;
	line-height: .18rem;
}

.meta-list dd {
	margin: 0;
	overflow: hidden;
	color: var(--support-text);
	font-size: .175rem;
	font-weight: 700;
	line-height: .22rem;
	text-overflow: ellipsis;
	white-space: nowrap;
}

.tips-card {
	display: flex;
	min-height: 0;
	flex: 1;
	flex-direction: column;
	padding: .16rem;
}

.tips-card h3 {
	margin: 0 0 .1rem;
	color: var(--support-text);
	font-size: .175rem;
	font-weight: 800;
	line-height: .23rem;
}

.quick-list {
	display: flex;
	min-height: 0;
	flex-direction: column;
	gap: .09rem;
	overflow: auto;
}

.quick-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	min-height: .42rem;
	border: 1px solid var(--support-line);
	border-radius: .08rem;
	padding: .08rem .12rem;
	color: var(--support-text);
	font-size: .175rem;
	line-height: .22rem;
	text-align: left;
	background: #fff;
	cursor: pointer;
}

.quick-item.active,
.quick-item:hover {
	border-color: var(--support-primary);
	color: var(--support-primary-strong);
	background: var(--support-primary-soft);
}

.quick-item small {
	color: var(--support-muted);
	font-size: .175rem;
}

.workspace {
	display: flex;
	min-width: 0;
	min-height: 7.92rem;
	flex-direction: column;
	overflow: hidden;
}

.tabs {
	display: flex;
	flex: 0 0 auto;
	height: .56rem;
	border-bottom: 1px solid var(--support-line-soft);
	padding: 0 .12rem;
	background: #fff;
}

.tab {
	position: relative;
	display: inline-flex;
	align-items: center;
	gap: .06rem;
	height: .56rem;
	border: 0;
	padding: 0 .12rem;
	color: var(--support-muted);
	font-size: .21rem;
	background: transparent;
	cursor: pointer;
}

.tab.active {
	color: var(--support-primary-strong);
	font-weight: 800;
}

.tab.active::after {
	content: "";
	position: absolute;
	right: .12rem;
	bottom: 0;
	left: .12rem;
	height: 2px;
	border-radius: 2px;
	background: var(--support-primary);
}

.tab em {
	min-width: .22rem;
	height: .22rem;
	border-radius: .1rem;
	padding: 0 .06rem;
	color: #fff;
	font-size: .175rem;
	font-style: normal;
	line-height: .22rem;
	text-align: center;
	background: #ff4d4f;
	box-shadow: 0 .04rem .1rem rgba(255, 77, 79, .22);
}

.tab-icon {
	width: .17rem;
	height: .17rem;
	background: currentColor;
}

.submit-icon {
	mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 13h8v2H8v-2zm0 4h6v2H8v-2z'/%3E%3C/svg%3E") center / contain no-repeat;
	-webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8l-6-6zm-1 1.5L18.5 9H13V3.5zM8 13h8v2H8v-2zm0 4h6v2H8v-2z'/%3E%3C/svg%3E") center / contain no-repeat;
}

.list-icon {
	mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11zM8 8v2h8V8H8zm0 4v2h8v-2H8zm0 4v2h5v-2H8z'/%3E%3C/svg%3E") center / contain no-repeat;
	-webkit-mask: url("data:image/svg+xml,%3Csvg viewBox='0 0 24 24' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M4 6.5A2.5 2.5 0 0 1 6.5 4h11A2.5 2.5 0 0 1 20 6.5v11a2.5 2.5 0 0 1-2.5 2.5h-11A2.5 2.5 0 0 1 4 17.5v-11zM8 8v2h8V8H8zm0 4v2h8v-2H8zm0 4v2h5v-2H8z'/%3E%3C/svg%3E") center / contain no-repeat;
}

.view {
	display: flex;
	min-height: 0;
	flex: 1;
	flex-direction: column;
	/* overflow: auto; */
	padding: .18rem;
	background: #fff;
}

.view.detail-host {
	padding: 0;
	overflow: hidden;
}

.tickets-view {
	display: flex;
	min-height: 0;
	flex: 1;
	flex-direction: column;
}

.list-header {
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	gap: .12rem;
}

.list-header h1 {
	margin: 0;
	color: var(--support-text);
	font-size: .175rem;
	font-weight: 800;
	line-height: .32rem;
}

.list-header p {
	margin: .03rem 0 0;
	color: var(--support-muted);
	font-size: .175rem;
	line-height: .22rem;
}

.ghost-button,
.load-more {
	height: .4rem;
	border: 1px solid var(--support-line);
	border-radius: .08rem;
	padding: 0 .18rem;
	color: #42526a;
	font-size: .175rem;
	font-weight: 700;
	background: #fff;
	cursor: pointer;
}

.ghost-button.compact {
	min-width: .88rem;
	height: .4rem;
	padding: 0 .12rem;
}

.ticket-list {
	display: flex;
	min-height: 0;
	flex: 1;
	flex-direction: column;
	gap: .1rem;
	overflow: auto;
	margin-top: .16rem;
}

.empty-state {
	display: flex;
	align-items: center;
	justify-content: center;
	flex: 1;
	flex-direction: column;
	min-height: 1.4rem;
	margin-top: .12rem;
	border: 1px dashed var(--support-line);
	border-radius: .08rem;
	color: var(--support-muted);
	background: #fbfdff;
}

.empty-state strong {
	color: var(--support-text);
	font-size: .175rem;
	line-height: .26rem;
}

.empty-state p {
	margin: .04rem 0 0;
	font-size: .175rem;
	line-height: .22rem;
}

.load-more {
	display: none;
	align-self: center;
	margin-top: .1rem;
	color: var(--support-primary-strong);
}

.load-more.visible {
	display: inline-flex;
	align-items: center;
}

.support-loading {
	display: flex;
	align-items: center;
	justify-content: center;
	gap: .08rem;
	padding: .12rem 0 0;
	color: var(--support-muted);
	font-size: .175rem;
}

.survey-mask {
	position: fixed;
	z-index: 3000;
	inset: 0;
	display: flex;
	align-items: center;
	justify-content: center;
	padding: .24rem;
	background: rgba(15, 23, 42, .46);
	backdrop-filter: blur(3px);
}

.survey-dialog {
	position: relative;
	box-sizing: border-box;
	width: 4.1rem;
	max-width: calc(100vw - .48rem);
	border: 1px solid rgba(255, 255, 255, .72);
	border-radius: .16rem;
	padding: .24rem .2rem .2rem;
	background: #fff;
	box-shadow: 0 .22rem .64rem rgba(15, 23, 42, .26);
}

.survey-close {
	position: absolute;
	top: .16rem;
	right: .16rem;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: .28rem;
	height: .28rem;
	border: 0;
	padding: 0;
	color: #6b778c;
	font-size: .2rem;
	line-height: 1;
	background: transparent;
	cursor: pointer;
}

.survey-main {
	display: flex;
	align-items: flex-start;
	gap: .14rem;
	padding-right: .26rem;
}

.survey-icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	flex: 0 0 .44rem;
	width: .44rem;
	height: .44rem;
	border-radius: .12rem;
	color: #fff;
	font-size: .22rem;
	font-weight: 800;
	line-height: 1;
	background: var(--support-primary);
	box-shadow: 0 .08rem .18rem color-mix(in srgb, var(--support-primary, #D26A2C) 24%, transparent);
}

.survey-copy {
	min-width: 0;
}

.survey-copy h3 {
	margin: 0;
	color: var(--support-text);
	font-size: .21rem;
	font-weight: 800;
	line-height: .28rem;
}

.survey-copy p {
	margin: .05rem 0 0;
	color: var(--support-muted);
	font-size: .175rem;
	line-height: .24rem;
}

.survey-actions {
	display: grid;
	grid-template-columns: 1fr 1fr;
	gap: .12rem;
	margin-top: .2rem;
}

.survey-button {
	height: .46rem;
	border-radius: .08rem;
	font-size: .175rem;
	font-weight: 800;
	line-height: .46rem;
	cursor: pointer;
}

.survey-button.ghost {
	border: 1px solid var(--support-line);
	color: #42526a;
	background: #fff;
}

.survey-button.primary {
	border: 1px solid var(--support-primary);
	color: #fff;
	background: var(--support-primary);
	box-shadow: 0 .08rem .18rem color-mix(in srgb, var(--support-primary, #D26A2C) 24%, transparent);
}

::-webkit-scrollbar {
	width: 5px;
	height: 5px;
}

::-webkit-scrollbar-thumb {
	border-radius: 5px;
	background: rgba(101, 116, 139, .28);
}

@media (max-width: 767px) {
	.main .box {
		width: 10rem;
		min-height: 100vh;
		overflow-x: hidden;
		background: #f4f4f4;
	}

	.main .item_Options {
		width: 10rem;
		min-height: 0;
		padding: 0 0 .46rem;
	}

	.main .content {
		width: 10rem;
		min-height: 0;
		margin: auto;
		margin-top: 0;
	}

	.support-workbench {
		display: block;
		width: 9.2rem;
		max-width: none;
		margin: .18rem auto 0;
		font-size: .32rem;
	}

	.side-panel {
		display: none;
	}

	.workspace {
		height: 15.5rem;
		min-height: 0;
		border-color: #d7e4f2;
		border-radius: .16rem;
		background: #fff;
		box-shadow: 0 .06rem .18rem rgba(16, 28, 46, .08);
	}

	.workspace.chat-workspace {
		height: 10.9rem;
	}

	.tabs {
		height: 1.12rem;
		padding: 0 .26rem;
		overflow-x: auto;
		border-bottom-color: #e6edf6;
		scrollbar-width: none;
	}

	.tabs::-webkit-scrollbar {
		display: none;
	}

	.tab {
		flex: 0 0 auto;
		height: 1.12rem;
		gap: .12rem;
		padding: 0 .18rem;
		color: #7a8798;
		font-size: .34rem;
		line-height: 1.12rem;
		white-space: nowrap;
	}

	.tab-icon {
		width: .28rem;
		height: .28rem;
	}

	.tab em {
		min-width: .48rem;
		height: .48rem;
		border-radius: .24rem;
		padding: 0 .12rem;
		font-size: .24rem;
		line-height: .48rem;
	}

	.view {
		height: calc(100% - 1.12rem);
		flex: 1;
		padding: .34rem .32rem 0;
		/* overflow: auto; */
		background: #f8fbff;
	}

	.view.detail-host {
		height: calc(100% - 1.12rem);
		padding: 0;
		overflow: hidden;
	}

	.tickets-view {
		min-height: 100%;
		flex: 1;
	}

	.list-header {
		align-items: center;
		gap: .2rem;
		flex: 0 0 auto;
		margin-bottom: .24rem;
	}

	.list-header h1 {
		font-size: .44rem;
		line-height: .56rem;
		font-weight: 800;
	}

	.list-header p {
		margin-top: .04rem;
		font-size: .28rem;
		line-height: .36rem;
	}

	.ghost-button.compact {
		height: .86rem;
		min-width: 1.7rem;
		border-radius: .14rem;
		padding: 0 .26rem;
		color: #42526a;
		font-size: .32rem;
	}

	.ticket-list {
		flex: 1;
		min-height: 0;
		overflow: auto;
		gap: .2rem;
		margin-top: 0;
		padding-bottom: .28rem;
	}

	.load-more {
		align-self: center;
		justify-content: center;
		min-width: 2.4rem;
		height: .72rem;
		margin: .22rem auto .28rem;
		border-radius: .36rem;
		padding: 0 .36rem;
		font-size: .28rem;
		line-height: .72rem;
	}

	.empty-state {
		margin-top: 0;
		min-height: 4rem;
	}

	.support-loading {
		padding-bottom: .24rem;
	}

	.survey-mask {
		align-items: flex-start;
		padding: 4.42rem .7rem 0;
		background: rgba(22, 31, 45, .48);
		backdrop-filter: blur(4px);
	}

	.survey-dialog {
		width: 8.7rem;
		max-width: 100%;
		border-radius: .26rem;
		padding: .46rem .42rem .4rem;
		box-shadow: 0 .28rem .86rem rgba(15, 23, 42, .28);
	}

	.survey-close {
		top: .34rem;
		right: .34rem;
		width: .42rem;
		height: .42rem;
		font-size: .34rem;
	}

	.survey-main {
		gap: .28rem;
		padding-right: .54rem;
	}

	.survey-icon {
		flex-basis: .88rem;
		width: .88rem;
		height: .88rem;
		border-radius: .24rem;
		font-size: .42rem;
		box-shadow: 0 .12rem .28rem color-mix(in srgb, var(--support-primary, #D26A2C) 25%, transparent);
	}

	.survey-copy h3 {
		font-size: .42rem;
		line-height: .54rem;
	}

	.survey-copy p {
		margin-top: .08rem;
		font-size: .31rem;
		line-height: .42rem;
	}

	.survey-actions {
		gap: .26rem;
		margin-top: .42rem;
	}

	.survey-button {
		height: 1.08rem;
		border-radius: .16rem;
		font-size: .34rem;
		line-height: 1.08rem;
	}
}
</style>
